import { create } from 'zustand'
import { getAxiosInstance } from '../network/axiosInstance'

// Export types for consistency
export interface NodeHistoryItem {
  id: string
  nodeId: string
  projectId: string
  saveName: string
  nodeData: any
  nodeType: string
  savedAt: string
  savedBy: string
  saveType: 'manual' | 'auto' | 'ai_processing'
  description?: string
}

interface NodeHistoryState {
  history: NodeHistoryItem[]
  isLoading: boolean
  error: string | null

  // Actions
  saveNodeState: (params: {
    projectId: string
    nodeId: string
    saveName: string
    description?: string
    nodeData: any
    nodeType: string
    saveType?: 'manual' | 'auto' | 'ai_processing'
  }) => Promise<void>

  getNodeHistory: (projectId: string, nodeId: string) => Promise<NodeHistoryItem[]>

  getProjectNodeHistory: (projectId: string) => Promise<NodeHistoryItem[]>

  getNodeState: (historyId: string) => Promise<NodeHistoryItem & { nodeData: any }>

  deleteNodeState: (historyId: string) => Promise<void>
}

export const useNodeHistoryStore = create<NodeHistoryState>((set) => ({
  history: [],
  isLoading: false,
  error: null,

  saveNodeState: async (params) => {
    try {
      set({ isLoading: true, error: null })

      await getAxiosInstance().post(
        `api/projects/${params.projectId}/nodes/${params.nodeId}/history`,
        {
          saveName: params.saveName,
          description: params.description,
          nodeData: params.nodeData,
          nodeType: params.nodeType,
          saveType: params.saveType || 'manual'
        }
      )

      set({ isLoading: false })
    } catch (error) {
      console.error('Error saving node state:', error)
      set({
        error: error instanceof Error ? error.message : 'Failed to save node state',
        isLoading: false
      })
      throw error
    }
  },

  getNodeHistory: async (projectId: string, nodeId: string) => {
    try {
      set({ isLoading: true, error: null })

      const response = await getAxiosInstance().get(
        `api/projects/${projectId}/nodes/${nodeId}/history`
      )

      const historyData = response.data || []
      set({
        history: historyData,
        isLoading: false
      })

      return historyData
    } catch (error) {
      console.error('Error fetching node history:', error)
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch node history',
        isLoading: false
      })
      throw error
    }
  },

  getProjectNodeHistory: async (projectId: string) => {
    try {
      set({ isLoading: true, error: null })

      const response = await getAxiosInstance().get(
        `api/projects/${projectId}/nodes/history/${projectId}`
      )

      const historyData = response.data || []
      set({
        history: historyData,
        isLoading: false
      })

      return historyData
    } catch (error) {
      console.error('Error fetching project node history:', error)
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch project node history',
        isLoading: false
      })
      throw error
    }
  },

  getNodeState: async (historyId: string) => {
    try {
      set({ isLoading: true, error: null })

      const response = await getAxiosInstance().get(
        `api/nodes/history/${historyId}`
      )

      set({ isLoading: false })
      return response.data
    } catch (error) {
      console.error('Error fetching node state:', error)
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch node state',
        isLoading: false
      })
      throw error
    }
  },

  deleteNodeState: async (historyId: string) => {
    try {
      set({ isLoading: true, error: null })

      await getAxiosInstance().delete(`api/nodes/history/${historyId}`)

      set({ isLoading: false })
    } catch (error) {
      console.error('Error deleting node state:', error)
      set({
        error: error instanceof Error ? error.message : 'Failed to delete node state',
        isLoading: false
      })
      throw error
    }
  }
}))

// Export individual hooks for backward compatibility
export const useSaveNodeState = () => {
  const store = useNodeHistoryStore()
  return {
    execute: store.saveNodeState,
    loading: store.isLoading,
    error: store.error
  }
}

export const useGetNodeHistory = () => {
  const store = useNodeHistoryStore()
  return {
    execute: store.getNodeHistory,
    loading: store.isLoading,
    error: store.error
  }
}

export const useGetNodeState = () => {
  const store = useNodeHistoryStore()
  return {
    execute: store.getNodeState,
    loading: store.isLoading,
    error: store.error
  }
}

export const useDeleteNodeState = () => {
  const store = useNodeHistoryStore()
  return {
    execute: store.deleteNodeState,
    loading: store.isLoading,
    error: store.error
  }
}
